import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Paper,
  Divider,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { authApi } from "../../utils/api";

export const UserProfile: React.FC = () => {
  const {
    user,
    isLoading,
    error: authError,
    setError: setAuthError,
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLocalError(null);
    setSuccess(null);
    setAuthError(null);

    try {
      await authApi.updateProfile(formData);
      setSuccess("Profile updated successfully");
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "100vh",
        padding: 4,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
          maxWidth: 600,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Avatar
            src={user.avatarUrl}
            alt={user.name}
            sx={{ width: 80, height: 80 }}
          />
          <Box>
            <Typography variant="h5" gutterBottom>
              Profile Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your account information
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {authError && <Alert severity="error">{authError}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
            />

            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              disabled={user.authProvider === "google"}
              helperText={
                user.authProvider === "google"
                  ? "Email cannot be changed for Google accounts"
                  : ""
              }
            />

            <Button
              type="submit"
              variant="contained"
              disabled={loading || isLoading}
              sx={{ mt: 2 }}
            >
              {loading || isLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Save Changes"
              )}
            </Button>
          </Box>
        </form>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Account Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Account Type: {user.authProvider === "google" ? "Google" : "Email"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Member since:{" "}
            {user.createdAt
              ? new Date(user.createdAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "N/A"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last login:{" "}
            {user.lastLogin
              ? new Date(user.lastLogin).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "N/A"}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};
